import React, { useState, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../contexts/AuthContext";
import { getApiBaseURL } from "../utils/api";

const MigrationButton = ({ document, onMigrationComplete }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState(
    document.migration_status
  );

  // Get tenant_id and user_id from AuthContext (HIPAA compliant)
  const { tenant_id, user } = useContext(AuthContext);

  // Get API base URL for proper backend communication
  const apiBase = getApiBaseURL();

  const handleMigrate = async () => {
    setIsLoading(true);
    try {
      const user_id = user?.user_id;

      if (!tenant_id || !user_id) {
        throw new Error(
          "Missing tenant_id or user_id from authentication context"
        );
      }

      console.log("🔄 Starting migration for document:", document.document_id);
      console.log("📋 Request data:", { user_id, tenant_id });

      const response = await axios.post(
        `${apiBase}/api/migration/migrate/${document.document_id}`,
        {
          user_id: user_id,
          tenant_id: tenant_id,
        }
      );

      console.log("✅ Migration response:", response.data);

      if (response.data.success) {
        setMigrationStatus("completed");
        if (onMigrationComplete) {
          onMigrationComplete(document.document_id, "google");
        }
      } else {
        throw new Error(
          "Migration failed: " + (response.data.error || "Unknown error")
        );
      }
    } catch (error) {
      console.error("❌ Migration failed:", error);
      setMigrationStatus("failed");

      // More descriptive error messages
      let errorMessage = "Migration failed. ";
      if (error.response?.status === 404) {
        errorMessage +=
          "Migration endpoint not found. Please check if migration routes are set up.";
      } else if (error.response?.status === 500) {
        errorMessage += "Server error. Please check the server logs.";
      } else if (error.message.includes("tenant_id")) {
        errorMessage += "Missing tenant information.";
      } else {
        errorMessage +=
          error.response?.data?.error || error.message || "Please try again.";
      }

      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ ENHANCED: Check multiple conditions for Google Cloud documents
  const isOnGoogle =
    document.storage_provider === "google" ||
    migrationStatus === "completed" ||
    document.migration_status === "completed" ||
    (document.file_path && document.file_path.startsWith("gs://"));

  // ✅ SIMPLIFIED: If document is on Google Cloud, return nothing (no button needed)
  // The main badge in DocumentList.jsx will show "Google Cloud"
  if (isOnGoogle) {
    return null; // Don't render anything - let the main badge handle it
  }

  // Show loading status if migration in progress
  if (migrationStatus === "in_progress" || isLoading) {
    return (
      <div className="d-flex align-items-center">
        <div
          className="spinner-border spinner-border-sm text-primary me-2"
          role="status"
        >
          <span className="visually-hidden">Migrating...</span>
        </div>
        <small className="text-muted">Migrating...</small>
      </div>
    );
  }

  // Show retry button if migration failed
  if (migrationStatus === "failed") {
    return (
      <button
        className="btn btn-outline-warning btn-sm"
        onClick={handleMigrate}
        disabled={isLoading}
        title="Migration failed - click to retry"
      >
        <i className="fas fa-redo me-1"></i>
        Retry Migration
      </button>
    );
  }

  // Default: show migrate button for AWS documents only
  return (
    <button
      className="btn btn-outline-primary btn-sm"
      onClick={handleMigrate}
      disabled={isLoading}
      title="Migrate this document to Google Cloud Storage"
    >
      <i className="fas fa-cloud-upload-alt me-1"></i>
      Migrate to Google
    </button>
  );
};

export default MigrationButton;
